import { SetStateAction, useEffect, useState } from "react";
import List from "../../components/List/List";
import {
  getApplicationVersions,
  getApplicationVersionsCount,
} from "../../api/applicationApi";
import ForwardButton from "../../components/ForwardButton/ForwardButton";
import {
  COLUMN_TYPE,
  DEFAULT_PAGE_SIZE,
  FILTER_ACTION_TYPE,
  FILTER_INPUT_TYPE,
} from "../../constants";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Loader from "../Loader/Loader";
import { SortProperty } from "../SortingSelector/SortingSelector";

interface ApplicationVersionListProps {
  applicationId: number;
}

export interface ApplicationVersionFilter {
  name?: string;
  processorList?: string;
  platformList?: string;
}

export default function ApplicationVersionList(
  props: ApplicationVersionListProps
) {
  const [applicationVersions, setApplicationVersions] = useState<any>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [numberOfApplicationVersions, setNumberOfApplicationVersions] =
    useState(-1);
  const [currentSort, setCurrentSort] = useState<SortProperty>();
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [filter, setFilter] = useState<ApplicationVersionFilter | null>(null);
  const [filterPropertyName, setFilterPropertyName] = useState("");
  const [filterPropertyProcessorList, setFilterPropertyProcessorList] =
    useState("");
  const [filterPropertyPlatformList, setFilterPropertyPlatformList] =
    useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setApplicationVersions(null);

    getApplicationVersions(
      props.applicationId,
      currentPage * pageSize,
      pageSize,
      sortField,
      sortOrder,
      filter && filter.name ? filter.name : "",
      filter && filter.processorList ? filter.processorList : "",
      filter && filter.platformList ? filter.platformList : "",
      (responseData: SetStateAction<any>) => {
        let applicationVersionsWithAppendedActions: any = [];
        for (let applicationVersion of responseData) {
          applicationVersionsWithAppendedActions.push(
            Object.assign(
              {
                action: (
                  <ForwardButton
                    linkTo={
                      "/application/" +
                      props.applicationId +
                      "/version/" +
                      applicationVersion.id
                    }
                  />
                ),
              },
              applicationVersion
            )
          );
        }

        if (
          applicationVersionsWithAppendedActions.length === 0 &&
          currentPage > 0
        ) {
          setCurrentPage(0);
        } else {
          setApplicationVersions(applicationVersionsWithAppendedActions);
        }
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [
    props.applicationId,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    filter,
  ]);

  useEffect(() => {
    getApplicationVersionsCount(
      props.applicationId,
      filter && filter.name ? filter.name : "",
      filter && filter.processorList ? filter.processorList : "",
      filter && filter.platformList ? filter.platformList : "",
      (responseData: SetStateAction<any>) => {
        setNumberOfApplicationVersions(responseData);
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [props.applicationId, filter]);

  const handleChange = (event: any) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    if (name === "name") {
      setFilterPropertyName(value);
    }
  };

  const handleFilterChange = (action: string) => {
    if (action === FILTER_ACTION_TYPE.CLEAR) {
      setFilterPropertyName("");
      setFilterPropertyProcessorList("");
      setFilterPropertyPlatformList("");
      setFilter({});
    }
    if (action === FILTER_ACTION_TYPE.APPLY) {
      let list = filterPropertyProcessorList.split(',').filter(Boolean);

      if (list.includes("CVEND_GEN_I") && !list.includes("CVEND")) {
        list.push("CVEND");
      }
      
      const finalProcessorList = list.join(',');

      setFilter({
        name: filterPropertyName,
        processorList: finalProcessorList,
        platformList: filterPropertyPlatformList,
      });
    }
  };

  if (error) {
    return <ErrorMessage text={error} />;
  }

  return (
    <>
      {applicationVersions && numberOfApplicationVersions >= 0 ? (
        <List
          columns={[
            {
              propertyName: "name",
              headerName: "Version",
              type: COLUMN_TYPE.TEXT,
            },
            {
              propertyName: "platform",
              headerName: "Platform",
              type: COLUMN_TYPE.TEXT,
            },
            {
              propertyName: "createdOn",
              headerName: "Created On",
              type: COLUMN_TYPE.DATE,
              hidden: true,
              sortable: true,
            },
            {
              propertyName: "action",
              type: COLUMN_TYPE.COMPONENT,
            },
          ]}
          items={applicationVersions}
          currentPage={currentPage}
          pageSize={pageSize}
          totalNumberOfItems={numberOfApplicationVersions}
          onPageChange={(page: number) => {
            setCurrentPage(page);
          }}
          onPageSizeChange={(pageSize: number) => {
            setCurrentPage(0);
            setPageSize(pageSize);
          }}
          currentSort={currentSort}
          onSortChange={(sortField: string, sortOrder: string) => {
            setCurrentSort({
              propertyName: sortField,
              type: sortOrder,
            });
            setSortField(sortField);
            setSortOrder(sortOrder);
          }}
          filterInputs={[
            {
              type: FILTER_INPUT_TYPE.TEXT,
              propertyName: "name",
              propertyLabel: "Name",
              value: filterPropertyName,
              onChange: handleChange,
            },
            {
              type: FILTER_INPUT_TYPE.MULTI_SELECT,
              propertyName: "processorList",
              propertyLabel: "Processor",
              options: [
                {
                  name: "pax",
                  label: "PAX",
                  value: "PAX",
                },
                {
                  name: "castles",
                  label: "Castles",
                  value: "CASTLES",
                },
                {
                  name: "cvend_gen_I",
                  label: "cVEND Gen I",
                  value: "CVEND_GEN_I",
                },
                {
                  name: "cvend_gen_II",
                  label: "cVEND Gen II",
                  value: "CVEND_GEN_II",
                },
              ],
              value: filterPropertyProcessorList,
              onChange: setFilterPropertyProcessorList,
            },
            {
              type: FILTER_INPUT_TYPE.MULTI_SELECT,
              propertyName: "platformList",
              propertyLabel: "Platform",
              options: [
                {
                  name: "android",
                  label: "Android",
                  value: "ANDROID",
                },
                {
                  name: "linux",
                  label: "Linux",
                  value: "LINUX",
                },
              ],
              value: filterPropertyPlatformList,
              onChange: setFilterPropertyPlatformList,
            },
          ]}
          onFilterChange={(action: string) => {
            handleFilterChange(action);
          }}
        />
      ) : (
        <Loader />
      )}
    </>
  );
}
